import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

const initialState: {
  selectedChatRoomId: number | null
  lastViewedChatRoomId: number | null
  chatListView: 'list' | 'conversation'
  unreadCounts: Record<number, number>
} = {
  selectedChatRoomId: null,
  lastViewedChatRoomId: null,
  chatListView: 'list', // 'list' for mobile, 'conversation' for desktop split view
  unreadCounts: {},
}

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setSelectedChatRoomId: (state, action: PayloadAction<number | null>) => {
      state.selectedChatRoomId = action.payload
      if (action.payload) {
        state.lastViewedChatRoomId = action.payload
        // clear unread count when selecting a chat
        delete state.unreadCounts[action.payload]
      }
    },
    setChatListView: (
      state,
      action: PayloadAction<'list' | 'conversation'>
    ) => {
      state.chatListView = action.payload
    },
    setUnreadCount: (
      state,
      action: PayloadAction<{ chatRoomId: number; count: number }>
    ) => {
      const { chatRoomId, count } = action.payload
      if (count > 0) {
        state.unreadCounts[chatRoomId] = count
      } else {
        delete state.unreadCounts[chatRoomId]
      }
    },
    clearUnreadCount: (state, action: PayloadAction<number>) => {
      delete state.unreadCounts[action.payload]
    },
    clearSelectedChat: (state) => {
      state.selectedChatRoomId = null
    },
    // reset all chat state (can use for logout)
    resetChatState: (state) => {
      state.selectedChatRoomId = null
      state.lastViewedChatRoomId = null
      state.unreadCounts = {}
    },
  },
})

export const selectChat = (state: any): typeof initialState => state.chat

export const selectSelectedChatRoomId = createSelector(
  selectChat,
  (chat) => chat.selectedChatRoomId
)

export const selectLastViewedChatRoomId = createSelector(
  selectChat,
  (chat) => chat.lastViewedChatRoomId
)

export const selectChatListView = createSelector(
  selectChat,
  (chat) => chat.chatListView
)

export const selectUnreadCounts = createSelector(
  selectChat,
  (chat) => chat.unreadCounts
)

export const selectUnreadCountForChat = (chatRoomId: number) =>
  createSelector(
    selectUnreadCounts,
    (unreadCounts) => unreadCounts[chatRoomId] || 0
  )

export const selectTotalUnreadCount = createSelector(
  selectUnreadCounts,
  (unreadCounts) =>
    Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)
)

export const {
  setSelectedChatRoomId,
  setChatListView,
  setUnreadCount,
  clearUnreadCount,
  clearSelectedChat,
  resetChatState,
} = chatSlice.actions

export default chatSlice.reducer
